import { ReactElement } from "react";
import { DataOrModifiedFn, LazyDataOrModifiedFn } from "use-async-resource";

type DataReader<T> = LazyDataOrModifiedFn<T> | DataOrModifiedFn<T>;

export default function Reader<R>({
  reader,
  children,
}: {
  reader: DataReader<R>;
  children: (data: R) => ReactElement | null;
}): ReactElement | null {
  const data = reader();
  return data !== undefined ? children(data) : null;
}

export function LazyReader<R>({
  reader,
  children,
}: {
  reader: DataReader<R>;
  children: (data: R | undefined) => ReactElement | null;
}): ReactElement | null {
  return children(reader());
}

export function Readers<R>({
  readers,
  children,
}: {
  readers: [DataReader<R>];
  children: (arg1: R) => ReactElement | null;
}): ReactElement | null;

export function Readers<R1, R2>({
  readers,
  children,
}: {
  readers: [DataReader<R1>, DataReader<R2>];
  children: (arg1: R1, arg2: R2) => ReactElement | null;
}): ReactElement | null;

export function Readers<R1, R2, R3>({
  readers,
  children,
}: {
  readers: [DataReader<R1>, DataReader<R2>, DataReader<R3>];
  children: (arg1: R1, arg2: R2, arg3: R3) => ReactElement | null;
}): ReactElement | null;

export function Readers<R1, R2, R3, R4>({
  readers,
  children,
}: {
  readers: [DataReader<R1>, DataReader<R2>, DataReader<R3>, DataReader<R4>];
  children: (arg1: R1, arg2: R2, arg3: R3, arg4: R4) => ReactElement | null;
}): ReactElement | null;

export function Readers<R1, R2, R3, R4, R5>({
  readers,
  children,
}: {
  readers: [
    DataReader<R1>,
    DataReader<R2>,
    DataReader<R3>,
    DataReader<R4>,
    DataReader<R5>
  ];
  children: (
    arg1: R1,
    arg2: R2,
    arg3: R3,
    arg4: R4,
    arg5: R5
  ) => ReactElement | null;
}): ReactElement | null;

export function Readers<ArgTypes extends any[]>({
  readers,
  children,
}: {
  readers: ArgTypes;
  children: (...data: any[]) => ReactElement | null;
}): ReactElement | null {
  const data: any = readers.map((reader) => reader());
  return data.every(Boolean) ? children(...data) : null;
}

export function LazyReaders<ArgTypes extends any[]>({
  readers,
  children,
}: {
  readers: ArgTypes;
  children: (...data: any[]) => ReactElement | null;
}): ReactElement | null {
  const data: any = readers.map((reader) => reader());
  return children(...data);
}
